import { ContentfulNextJsImage } from '@components/common/ContentfulImage'
import { titleSizeToClass } from 'app/_components/ContentfulPage/contentfulPage.utils'
import { ContentfulMarkdown } from '@utils/contentful/ContentfulMarkdown'
import { IConfigurableFeature } from '../features.types'
import classNames from 'classnames'
import { StreamingVideoPlayer } from '@components/video/StreamingVideoPlayer'
import { useInView } from 'react-intersection-observer'
import { ComponentPlaceholder } from 'app/_components/ContentfulPage/ComponentPlaceholder'
import { ConfigurableCta } from 'app/_components/ContentfulPage/ConfigurableCta'
import { mapTextColorToClass } from 'app/_components/ContentfulPage/ConfigurableText/ConfigurableText.utils'

const PLACEHOLDER_HEIGHT = 460
export default function StandaloneHorizontalFeature({
  backgroundColor,
  title,
  titleColor,
  titleSize,
  subtitle,
  subtitleColor,
  description,
  ctaLabel,
  ctaColor,
  ctaType,
  ctaBtnVariant,
  ctaLink,
  ctaEventId,
  image,
  imageUrl,
  imageSide,
  imageSize,
  onCtaAction,
  streamingVideo,
  generateStatically = false,
}: IConfigurableFeature) {
  const { ref, inView } = useInView({
    triggerOnce: true,
    rootMargin: '100px 0px',
  })

  const dynamicStyles = {
    backgroundColor: backgroundColor,
  }
  const isFullWidthImage = imageSize === 'Full'

  const imageSideToClass = {
    left: 'order-1 rounded-0 md:rounded-r-[24px]',
    right: 'order-1 rounded-0 md:order-3 md:rounded-l-[24px]',
  }

  const onCtaClick = () => {
    if (onCtaAction) {
      onCtaAction({ ctaId: ctaEventId })
    }
  }

  return (
    <div
      ref={ref}
      className="flex flex-col w-full md:flex-row"
      style={dynamicStyles}
    >
      {generateStatically || inView ? (
        <>
          <div
            className={classNames(
              `w-full md:w-[57%] ${imageSideToClass[imageSide]} overflow-hidden aspect-[4/3] relative`,
              {
                'md:rounded-none aspect-auto min-h-[332px]': isFullWidthImage,
              }
            )}
          >
            {streamingVideo && (
              <StreamingVideoPlayer
                title={streamingVideo.title}
                playbackId={streamingVideo.video.playbackId}
                autoPlay={true}
                muted={true}
              />
            )}

            {!streamingVideo && image && (
              <ContentfulNextJsImage
                src={image?.imageUrl ?? imageUrl}
                alt={image?.imageTitle}
                fill={true}
                objectFit="cover"
                className="w-full"
                sizes="(max-width: 768px) 100vw, 60vw"
                quality={75}
                format="jpg"
              />
            )}
          </div>

          <div
            className={classNames(
              'w-full md:w-[43%] flex flex-col items-center md:items-start px-[20px] justify-center',
              'md:px-[32px] lg:px-[48px] order-2 py-[48px] md:py-[40px] lg:py-[48px]'
            )}
          >
            {subtitle && (
              <h5
                className={`text-center md:text-left text-[12px] leading-tight mb-4 ${
                  mapTextColorToClass[subtitleColor] || mapTextColorToClass.grey
                }`}
              >
                {subtitle}
              </h5>
            )}

            {title && (
              <h3
                className={`text-center md:text-left font-bold mb-4 ${
                  titleSizeToClass[titleSize] || titleSizeToClass.L
                } ${
                  mapTextColorToClass[titleColor] || mapTextColorToClass.purple
                }`}
              >
                {title}
              </h3>
            )}

            {description && (
              <div className="mb-[24px]">
                <ContentfulMarkdown overrideClass="text-center md:text-left text-[12px] lg:text-[14px] leading-normal text-boulder">
                  {description}
                </ContentfulMarkdown>
              </div>
            )}

            {ctaLabel && (
              <ConfigurableCta
                ctaLabel={ctaLabel}
                ctaType={ctaType}
                ctaBtnVariant={ctaBtnVariant}
                ctaColor={ctaColor}
                ctaLink={ctaLink}
                className="mx-auto md:mr-auto md:ml-0"
                onClick={onCtaClick}
              />
            )}
          </div>
        </>
      ) : (
        <ComponentPlaceholder
          height={PLACEHOLDER_HEIGHT}
          bgColor={backgroundColor}
        />
      )}
    </div>
  )
}
